import React, { useContext, useMemo } from 'react';
import { ascending, mean } from 'd3-array';

import PresentationContext from '../PresentationContext';
import TargetContext from './TargetContext';
import { glucoseAccessor, timeAccessor } from '../utils/dataUtils';
import { parseDate } from '../utils/formats';

import './Dashboard.css';
import { Fragment } from 'react';

function TabularDiaryView(props) {
  const {
    days,
    basalData = {},
    bolusData = {},
    longActingData = {},
    nutritionData = {},
    smbgData = {},
    t,
  } = props;

  const {
    formatDate,
    formatGlucose,
    formatGlucoseValue,
    formatHour,
    formatTimeString,    
    timeFormat,
  } = useContext(PresentationContext);

  const { targets } = useContext(TargetContext);

  const getGlucoseClass = useMemo(() => {
    return function glucoseClass(g) {
      const glucose = glucoseAccessor(g);
      return glucose > targets.top ? 'high' : (glucose < targets.bottom ? 'low' : 'inTarget');
    }
  }, [targets]);

  return (
    <section id="diary">
      <table className="tabularDiaryView">
        <thead>
          <tr>
            <th></th>
              {[...Array(24).keys()].map(h => (
                <th key={h}>{formatHour(new Date().setHours(h))}</th>
              ))}
          </tr>
        </thead>
        <tbody>
          {days.map(d => {

            // const basal = basalData[d];
            const bolus = bolusData[d] || [];
            const carbs = nutritionData[d] || [];
            const glucose = (smbgData[d] || [])
              .filter((g, i, a) => i >= (a.length - 1)
              || ((g.time !== a[i+1].time) && (g.glucose !== a[i+1].glucose)));
            const long = longActingData[d] || [];

            const basal = [ ...(basalData[d] || []), ...(longActingData[d] || [])];

            const averageGlucose = mean(glucose.map(glucoseAccessor).sort(ascending));
            const totalCarbs = carbs.reduce((t, c) => t + (c.carbohydrate || 0), 0);
            const totalShort = bolus.reduce((t, b) => t + (b.amount || 0), 0);
            const totalLong = basal.reduce((t, b) => t + (b.amount || 0), 0);

            return (
              <Fragment key={d}>
                <tr>
                  <td className="day" rowSpan="4">
                    {timeFormat('%A')(parseDate(d))} {formatDate(parseDate(d))}
                  </td>
                  {[...Array(24).keys()].map(h => (
                    <td key={h}>
                      {glucose.filter(d => timeAccessor(d).getHours() === h).map(g => (
                        <Fragment key={g.id || g.time}>
                          <data
                            className={getGlucoseClass(g)}
                            value={glucoseAccessor(g)}
                            title={`${formatTimeString(new Date(g.time))}\n${formatGlucose(glucoseAccessor(g))}`}
                          >
                            {formatGlucoseValue(glucoseAccessor(g))}
                          </data>
                          {' '}
                        </Fragment>
                      ))}
                    </td>
                  ))}
                  <td
                    className="summary"
                    dangerouslySetInnerHTML={
                      {__html: averageGlucose
                        ? formatGlucose(averageGlucose).replace(' ', '&nbsp;')
                        : ''
                      }
                    }
                  />
                </tr>
                <tr>
                  {[...Array(24).keys()].map(h => (
                    <td key={h}>
                      {carbs.filter(d => timeAccessor(d).getHours() === h).map(c => (
                        <Fragment key={c.id || c.time}>
                          <data
                            value={c.carbohydrate}
                          >
                            {c.carbohydrate?.toFixed(0)}
                          </data>
                          {' '}
                        </Fragment>
                      ))}
                    </td>
                  ))}
                  <td className="summary">{totalCarbs}&nbsp;g</td>
                </tr>
                <tr>
                  {[...Array(24).keys()].map(h => (
                    <td key={h}>
                      {bolus.filter(d => timeAccessor(d).getHours() === h).map(b => (
                        <Fragment key={b.id || b.time}>
                          <data
                            value={b.amount}
                          >
                            {b.amount?.toFixed(1)}
                          </data>
                          {' '}
                        </Fragment>
                      ))}
                    </td>
                  ))}
                  <td className="summary">{totalShort.toFixed(1)}&nbsp;{t('insulin.iu')}</td>
                </tr>
                <tr>
                  {[...Array(24).keys()].map(h => (
                    <td key={h}>
                      {long.filter(d => timeAccessor(d).getHours() === h).map(l => (
                        <Fragment key={l.id || l.time}>
                          <data
                            value={l.amount}
                          >
                            {l.amount?.toFixed(1)}
                          </data>
                          {' '}
                        </Fragment>
                      ))}
                    </td>
                  ))}
                  <td className="summary">{totalLong.toFixed(1)}&nbsp;{t('insulin.iu')}</td>
                </tr>
                <tr className="spacer">
                  <td></td>
                  <td colSpan="24">&nbsp;</td>
                  <td></td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th />
            {[...Array(24).keys()].map(h => (
              <th key={h}>{formatHour(new Date().setHours(h))}</th>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  );
};

export default TabularDiaryView;
